import { clamp, keyBy, uniqBy } from 'lodash';

//TODO: this is bad, but until these thunks are converted to epics it's necessary
import * as optionGroupAPI from 'domains/option_groups/option_groups.api';
import * as arrangementsAPI from 'domains/saved_arrangements/saved_arrangements.api';

import Arrangement from 'domains/arrangement';

import { selectZoomedPPI } from 'selectors/ppi';

import subcatalogsModule from 'domains/subcatalogs';
import * as subcatalogsAPI from 'domains/subcatalogs/subcatalogs.api';
import productsModule from 'domains/products';
import optionGroupsModule from 'domains/option_groups';
import photosModule from 'domains/photos';
import stage from 'domains/stage';
import canvasModule from 'domains/canvases';
import savedArrangements from 'domains/saved_arrangements';
import cropsModule from 'domains/crops';

import { getArrangementAvailability } from 'utils/arrangement_util';
import { notNull } from 'utils/type_guards';
import { createRotatedImageBlob, imageFilters } from 'utils/image_util';
import { fetchPhotoBlob } from 'utils/image_load';

import type { AppThunk } from 'store/wos_store';
import type { SavedArrangement } from 'domains/saved_arrangements';
import type { RestoreArrangementCropsPayload } from 'domains/crops';
import type { ImageFilter } from 'types/photo_types';
import type { SavedArrangementCanvas } from 'domains/saved_arrangements';

// Clamps the arrangement position within the bounds of the wall preview size
export function wallPreviewBoundsCheck(): AppThunk {
  return function boundsCheckThunk(dispatch, getState) {
    const state = getState();

    const ppi = selectZoomedPPI(state);
    const arrangementPosition = canvasModule.selectors.arrangementPosition(state);

    const withProducts = canvasModule.selectors.allWithProducts(state);
    const arrangement = Arrangement.createWithProducts(withProducts);

    const arrangementWidth = arrangement.width;
    const arrangementHeight = arrangement.height;

    const limits = stage.selectors.wallPreviewBounds(state);

    // Skip if limits are not defined yet
    if (!limits) return;

    let { leftLimit, rightLimit, topLimit, bottomLimit } = limits;

    leftLimit /= ppi;
    topLimit /= ppi;
    rightLimit = rightLimit / ppi - arrangementWidth;
    bottomLimit = bottomLimit / ppi - arrangementHeight;

    const arrangementRightEdge = arrangementPosition.x + arrangementWidth;
    const arrangementBottomEdge = arrangementPosition.y + arrangementHeight;

    const withinLeftBound = arrangementPosition.x > leftLimit;
    const withinRightBound = arrangementRightEdge < rightLimit;
    const withinTopBound = arrangementPosition.y > topLimit;
    const withinBottomBound = arrangementBottomEdge < bottomLimit;

    if (!withinRightBound || !withinLeftBound || !withinTopBound || !withinBottomBound) {
      const x = clamp(arrangementPosition.x, leftLimit, rightLimit);
      const y = clamp(arrangementPosition.y, topLimit, bottomLimit);

      dispatch(canvasModule.actions.setArrangementPosition({ x, y }));
    }
  };
}

export function prepareRestoreArrangement(arrangement: SavedArrangement): AppThunk {
  return async function prepareRestoreArrangementThunk(dispatch, getState) {
    let preState = getState();

    // Arrangements can only be composed of subcatalogs within one catalog
    // So, if we have the subcatalog of the first canvas, we will have all the
    // possible subcatalogs in this arrangement
    const firstCanvasConfig = arrangement.canvases[0].config;
    const subcatalog = subcatalogsModule.selectors.selectById(
      preState,
      firstCanvasConfig.subcatalog
    );

    // If we don't have the subcatalogs for this arrangement, fetch and add them to the store
    if (!subcatalog) {
      const catalogId = firstCanvasConfig.catalog;
      const response = await subcatalogsAPI.allByCatalogId(catalogId);
      dispatch(
        subcatalogsModule.actions.fetchSubcatalogs.fulfilled({
          subcatalogs: response,
          catalogId,
        })
      );
    }

    // We have the subcatalogs & products for sure now, let's check for option groups
    preState = getState();
    const uniqueProductCanvases = uniqBy(arrangement.canvases, (canvas) => canvas.config.product);
    const haveAllOptionGroups = uniqueProductCanvases.every((canvas) => {
      const {
        config: { product },
      } = canvas;
      const optionGroups = optionGroupsModule.selectors.forTemplateId(preState, product);
      return optionGroups && optionGroups.length > 0;
    });

    if (!haveAllOptionGroups) {
      const promises = uniqueProductCanvases.map((canvas) => {
        const {
          config: { product },
        } = canvas;
        return optionGroupAPI.allByTemplateId(product);
      });

      const allOptionGroupsAndOptions = await Promise.all(promises);

      allOptionGroupsAndOptions.forEach((joined, index) => {
        dispatch(
          optionGroupsModule.actions.fetchOptionGroups.fulfilled({
            optionGroups: joined,
            templateId: uniqueProductCanvases[index].config.product,
          })
        );
      });
    }

    preState = getState();
    const unavailable = getArrangementAvailability(preState, arrangement.canvases);

    if (unavailable.length > 0) {
      dispatch(
        savedArrangements.actions.addUnavailableArrangements({
          id: arrangement.id,
          items: unavailable,
        })
      );
    } else {
      dispatch(restoreArrangementAndAutoFit(arrangement));
    }
  };
}

export function restoreArrangementAndAutoFit(arrangement: SavedArrangement): AppThunk {
  return async function restoreArrangementThunk(dispatch, getState) {
    const state = getState();

    const optionGroupsById = optionGroupsModule.selectors.selectEntities(state);

    // Replace any hidden options, these should not prevent the restore just let the defaults become selected
    let hasChanged = false;
    const replaced = arrangement.canvases.map((canvas) => {
      const result = optionGroupsModule.utils.replaceHiddenOptions(
        optionGroupsById,
        canvas.config.options
      );

      if (result.hasChanged) {
        hasChanged = true;
      }

      return { ...canvas, config: { ...canvas.config, options: result.update } };
    });

    dispatch(canvasModule.actions.restoreArrangement({ ...arrangement, canvases: replaced }));

    let { crops } = arrangement;
    // Key crops by canvas ID to make later merging easy
    const cropsByCanvasId = keyBy(crops, 'canvas_id');

    // Match up photos with crops, and a promise for the crop's base image blob
    const photosAndCrops = crops
      .map((crop) => {
        const photo = photosModule.selectors.selectById(state, crop.ordering_upload_id);
        if (!photo) return null;

        return { crop, photo };
      })
      .filter(notNull)
      .map((merged) => {
        const { crop, photo } = merged;
        const appliedFilter = crop.filter_applied || 'original';
        const filter = imageFilters.find((f) => f.filterType === appliedFilter) as ImageFilter;
        const imageSrc = photo[filter.filePathFull];

        return { ...merged, blobPromise: fetchPhotoBlob(imageSrc) };
      });

    // Fetch all the base blobs
    const blobs = await Promise.all(photosAndCrops.map((merged) => merged.blobPromise));

    // Create promises for rotated crop image blobs
    const withRotationPromises = blobs.map((blob, index) => {
      // index will match the merged photosAndCrops array
      const matched = photosAndCrops[index];

      // If we don't need to rotate it, Promise.all will resolve with the original blob
      if (matched.crop.degrees_rotated === 0) return blob;

      const promise = createRotatedImageBlob(blob, matched.crop.degrees_rotated);
      return promise;
    });

    // Fetch all the rotated blobs, resolving the unrotated ones with the original
    const withRotatedBlobs = await Promise.all(withRotationPromises);

    // Merge the crop with the blob src
    const restoreCrops: RestoreArrangementCropsPayload[] = withRotatedBlobs.map((blob, index) => {
      // Again, index will match the merged photosAndCrops array
      const { canvas_id } = photosAndCrops[index].crop;
      const blobSrc = URL.createObjectURL(blob);

      return { ...cropsByCanvasId[canvas_id], blobSrc };
    });

    dispatch(cropsModule.actions.restoreArrangementCrops(restoreCrops));

    // This is the same message as the cart_product_editor constant, but this whole file
    // needs redone so we're doing it dirty
    if (hasChanged) {
      dispatch(
        stage.actions.setToastMessage('Previously selected options are no longer available.')
      );
    }

    const wallPreviewEnabled = stage.selectors.wallPreviewActive(state);
    if (wallPreviewEnabled) {
      dispatch(stage.actions.center());
    } else {
      dispatch(stage.actions.autofit());
    }
  };
}

export function updateUnavailableArrangement(
  removeIds: string[],
  savedArrangement: SavedArrangement
): AppThunk {
  return async function updateUnavailableArrangementThunk(dispatch, getState) {
    let state = getState();
    const savedCanvasesById = keyBy(savedArrangement.canvases, (s) => s.config.id);

    const arrangement = Arrangement.createFromSavedArrangement(savedArrangement.canvases);
    removeIds.forEach((id) => arrangement.removeCanvas(id));
    arrangement.recalculatePositions();

    arrangement.canvases.forEach((c) => {
      const p = productsModule.selectors.createProduct(state, c.product);
      p && arrangement.setProduct(c.id, p);
    });

    const canvasesForSave = arrangement.canvases.map((c) => ({
      width: savedCanvasesById[c.id]?.width,
      height: savedCanvasesById[c.id]?.height,
      x: c.position.x,
      y: c.position.y,
      config: c as SavedArrangementCanvas['config'], // Since we created from this canvas, we can be sure of this cast
    }));

    const keepCrops = savedArrangement.crops.filter((crop) => !!arrangement.find(crop.canvas_id));

    // Save the arrangement with updated canvases & size
    const resp = await arrangementsAPI.update(savedArrangement.id, {
      name: savedArrangement.name,
      width: arrangement.width,
      height: arrangement.height,
      canvases: canvasesForSave,
      crops: keepCrops,
    });

    // Send the update response to the reducer
    dispatch(savedArrangements.actions.update.fulfilled(resp));

    // Get the arrangement after putting it through reducer which normalizes the data
    state = getState();
    const toRestore = savedArrangements.selectors.selectById(
      state,
      savedArrangement.id
    ) as SavedArrangement;
    dispatch(restoreArrangementAndAutoFit(toRestore));
  };
}
